import { useState, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { courseReportService } from '../../Services/Reports/CourseReportService';
import { CourseReportSortBy } from 'Common/Constants/Enums/Individual/Reports/CourseSortEnum';

export default function useCourseReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({ SortBy: CourseReportSortBy.Latest });

  const formatDateForAPI = (date) => dayjs(date).format('YYYY-MM-DD');

  const fetchCourseReport = async () => {
    setLoading(true);
    try {
      const params = { PageNumber: pageNumber, PageSize: pageSize };

      if (filters.FromDate) params.FromDate = formatDateForAPI(filters.FromDate);
      if (filters.ToDate) params.ToDate = formatDateForAPI(filters.ToDate);
      if (filters.IsPublished === true || filters.IsPublished === false)
        params.IsPublished = filters.IsPublished;
      if (filters.IsPaid === true || filters.IsPaid === false)
        params.IsPaid = filters.IsPaid;
      if (filters.SortBy) params.SortBy = filters.SortBy;

      const res = await courseReportService.getCourseReport(params);
      setData(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || 'Failed to load course report');
    } finally {
      setLoading(false);
    }
  };

const downloadReport = async () => {
  try {
    message.loading({ content: 'Generating PDF...', key: 'dl' });
    
    const params = {
      PageNumber: pageNumber,
      PageSize: pageSize
    };

    if (filters.FromDate) params.FromDate = formatDateForAPI(filters.FromDate);
    if (filters.ToDate) params.ToDate = formatDateForAPI(filters.ToDate);
    if (filters.IsPublished === true || filters.IsPublished === false)
      params.IsPublished = filters.IsPublished;
    if (filters.IsPaid === true || filters.IsPaid === false)
      params.IsPaid = filters.IsPaid;
    if (filters.SortBy) params.SortBy = filters.SortBy;

    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Download timed out')), 60000) // 60 seconds timeout
    );
    
    const res = await Promise.race([
      courseReportService.downloadCourseReport(params),
      timeoutPromise
    ]);

    if (!res.data || res.data.size === 0) throw new Error('Server returned empty response');

    // Generate filename directly in frontend using the same pattern as backend
    const applicationName = "CoreIQPro";
    const dateStr = dayjs().format('MMM-DD-YYYY');
    const filename = `${applicationName}-Course-Report-${dateStr}.pdf`;

    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);

    message.success({ content: 'PDF downloaded', key: 'dl' });
  } catch (err) {
    console.error("Download Error:", err);
    message.error({ content: 'Download failed', key: 'dl' });
  }
};


  useEffect(() => {
    fetchCourseReport();
  }, [pageNumber, pageSize, filters.SortBy, filters.IsPublished, filters.IsPaid, filters.FromDate, filters.ToDate]);

  return {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    filters,
    setPageNumber,
    setPageSize,
    setFilters,
    fetchCourseReport,
    downloadReport
  };
}
